import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { CButton, CCol, CForm, CRow } from '@coreui/react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { AIInsightsDetailContext } from '../Popup/AIInsightsTemplate';
import CFormGroup from '../../../../../migration/CFormGroup';
import CLabel from '../../../../../migration/CLabel';
import { CInput } from '../../../../../migration/CInput';
import { CInvalidFeedback } from '../../../../../migration/CInvalidFeedback';
import StepCardHeader from '../Components/StepCardHeaders'
import { callTokenApi } from '../../../../../../apiCaller';
import { API_AI_INSIGHTS_TEMPLATES_BY_ACCOUNT_CREATE } from '../../../../../../constants';
import { useSelector } from 'react-redux';
import FormikStateObserver from '../Components/FormikStateObserver';
import isEqual from 'lodash/isEqual';
import Waiting from '../../../../../general/Loadings/Waiting';

const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required('Name is required').max(200, 'Name cannot be more than 200 characters'),
});

const FormikStateSyncObserver = ({ stepId, values, savedStepData, updateUnsavedEdits, handleSetUnsavedChanges }) => {
    const previousValuesRef = useRef(values);

    useEffect(() => {
        if (!isEqual(values, previousValuesRef.current)) {
            updateUnsavedEdits(stepId, {
                name: values.name,
                description: values.description
            });
            previousValuesRef.current = values;
        }
    }, [stepId, values, updateUnsavedEdits]);

    useEffect(() => {
        const currentStepValues = {
            name: values.name || '',
            description: values.description || ''
        };
        const isDirtyVsSaved = !isEqual(currentStepValues, savedStepData);
        handleSetUnsavedChanges(isDirtyVsSaved);

    }, [values, savedStepData, handleSetUnsavedChanges]);

    return null;
};

const FormGroup = ({
    name,
    label,
    errors,
    values,
    touched,
    ...props
}) => {
    return (
        <CFormGroup className="mb-3">
            <CLabel htmlFor={name}>{label}</CLabel>
            <CInput
                id={name}
                invalid={touched[name] && !!errors[name]}
                value={values[name]}
                {...props}
            />
            <CInvalidFeedback>{touched[name] && errors[name]}</CInvalidFeedback>
        </CFormGroup>
    );
}

const NameDescription = () => {
    const {
        activeStep,
        setActiveStep,
        stepsData,
        setStepsData,
        setViewTemplates,
        unsavedEdits,
        updateUnsavedEdits,
        clearUnsavedEditsForStep,
        handleSetUnsavedChanges,
        clearUnsavedChangesFlag,
    } = useContext(AIInsightsDetailContext);
    const formikRef = useRef(null);
    const activeAccount = useSelector((state) => state.subscriber.activeAccount);

    const initialValues = useMemo(() => {
        const base = {
            name: stepsData.name || '',
            description: stepsData.description || '',
        };
        const unsaved = unsavedEdits[activeStep] || {};
        return { ...base, ...unsaved };
    }, [stepsData.name, stepsData.description, activeStep, unsavedEdits[activeStep]]); // eslint-disable-line react-hooks/exhaustive-deps

    const savedStepData = useMemo(() => ({
        name: stepsData.name || '',
        description: stepsData.description || '',
    }), [stepsData.name, stepsData.description]);

    const onSubmit = useCallback(async (values) => {
        const trimmedName = values.name.trim();
        const trimmedDescription = values.description?.trim() || '';

        const hasNoChanges =
            trimmedName === stepsData.name &&
            trimmedDescription === stepsData.description;

        if (hasNoChanges) {
            setActiveStep(2);
            return;
        }

        const currentStep = 1;

        const payload = {
            name: trimmedName,
            description: trimmedDescription,
            accountId: activeAccount.accountId,
            lastEditStep: stepsData.lastEditStep && stepsData.lastEditStep > currentStep
                ? stepsData.lastEditStep
                : currentStep,
        };

        try {
            let response;
            const isUpdate = !!stepsData?.id;
          
            if (isUpdate) {
                response = await callTokenApi(
                    `${API_AI_INSIGHTS_TEMPLATES_BY_ACCOUNT_CREATE}/${stepsData.id}`,
                    'PUT',
                    payload
                );
            } else {
                response = await callTokenApi(
                    API_AI_INSIGHTS_TEMPLATES_BY_ACCOUNT_CREATE,
                    'POST',
                    payload
                );
            }

            const isSuccess = response?.data?.code === 200 || response?.data?.code === 201;
            const responseData = response?.data?.data;

            if (isSuccess && responseData) {
                setStepsData({
                    ...stepsData,
                    ...payload,
                    status: responseData.status || stepsData.status,
                    id: responseData.id || stepsData.id,
                });

                setViewTemplates((prev) => {
                    const existsIndex = prev.findIndex(tpl => tpl.id === responseData.id);
                
                    if (existsIndex !== -1) {
                        const updatedTemplates = [...prev];
                        updatedTemplates[existsIndex] =  { ...prev[existsIndex], ...responseData};
                        return updatedTemplates;
                    }
                
                    return [responseData, ...prev];
                });

                toast.success(`Template ${stepsData?.id ? 'updated' : 'created'} successfully.`);
                clearUnsavedEditsForStep(activeStep);
                clearUnsavedChangesFlag();
                setActiveStep(2);
            } else {
                toast.error('Failed to save the template. Please try again.');
            }
        } catch (err) {
            console.error('Submit error:', err);
            toast.error('Something went wrong. Please try again later.');
        }
    }, [setViewTemplates, setActiveStep, setStepsData, stepsData, clearUnsavedChangesFlag, clearUnsavedEditsForStep, activeAccount.accountId]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <StepCardHeader
            title={`Name & Description`}
            step={1}
            activeStep={activeStep}
            lastEditStep={stepsData.lastEditStep}
            setActiveStep={() => setActiveStep(1)}
        >
            <p>Create a friendly name and description for this AI Insights Template.</p>

            <Formik
                innerRef={formikRef}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
                validateOnBlur={true}
                enableReinitialize 
            >
                {({ values, errors, touched, dirty, handleChange, isSubmitting, handleSubmit }) => {
                    return (
                        <>
                            <FormikStateObserver dirty={dirty} onDirtyChange={handleSetUnsavedChanges} />
                            <FormikStateSyncObserver
                                stepId={activeStep}
                                values={values}
                                savedStepData={savedStepData}
                                updateUnsavedEdits={updateUnsavedEdits}
                                handleSetUnsavedChanges={handleSetUnsavedChanges}
                            />
                            
                            <CForm onSubmit={handleSubmit} noValidate>
                                <CRow>
                                    <CCol lg="12">
                                        <FormGroup
                                            name="name"
                                            label="Name"
                                            values={values}
                                            errors={errors}
                                            touched={touched}
                                            placeholder="Enter a name"
                                            onChange={handleChange}
                                        />
                                    </CCol>

                                    <CCol lg="12">
                                        <FormGroup
                                            name="description"
                                            label="Description"
                                            values={values}
                                            errors={errors}
                                            touched={touched}
                                            placeholder="Enter a description for your AI Insights Template"
                                            onChange={handleChange}
                                        />
                                    </CCol>
                                </CRow>

                                <CButton disabled={isSubmitting} type="submit" className="px-4" color="primary">
                                    <Waiting isLoading={isSubmitting}>Save & Next</Waiting>
                                </CButton>
                            </CForm>
                        </>
                    );
                }}
            </Formik>
        </StepCardHeader>
    );
};

export default NameDescription;
